import { Link } from 'react-router-dom';
import { ArrowRight, Award, Target, Eye, Briefcase, GraduationCap } from 'lucide-react';
import { Button } from '../components/ui/button';

const FOUNDER_IMAGE = "https://customer-assets.emergentagent.com/job_spirits-consulting/artifacts/xwh8nobz_IMG-20260614-WA0028.jpg";

export const About = () => {
  const experience = [
    {
      company: 'Diageo',
      role: 'Senior Manufacturing Leadership',
      period: 'Recent',
      description: 'Led manufacturing operations and transformation initiatives across multiple plants.',
    },
    {
      company: 'United Spirits',
      role: 'Operations Head',
      period: 'Previous',
      description: 'Managed end-to-end manufacturing and supply chain operations.',
    },
    {
      company: 'Shaw Wallace',
      role: 'Manufacturing Management',
      period: 'Earlier',
      description: 'Built foundation in AlcoBev manufacturing and distillery operations.',
    },
    {
      company: 'McKinsey & Company',
      role: 'Consultant',
      period: '2 Years',
      description: 'Strategic consulting expertise in operations and transformation.',
    },
  ];

  const objectives = [
    'Support clients in manufacturing and supply chain transformation across the AlcoBev and FMCG industries',
    'Improve operational efficiency, productivity, asset utilization, and cost competitiveness',
    'Enable safe, quality-driven, and compliant manufacturing operations',
    'Assist organizations in scaling up through structured capacity expansion',
    'Build leadership capability and strong operating systems for sustainable growth',
    'Integrate sustainability, energy efficiency, and governance into practices',
  ];

  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section data-testid="about-hero" className="relative py-20 lg:py-32 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.2em] text-blue-400 font-semibold mb-4">About Us</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight mb-6">
              Three Decades of<br />
              <span className="text-blue-400">Industry Excellence</span>
            </h1>
            <p className="text-lg lg:text-xl text-slate-300 leading-relaxed">
              Riya AlcoBev Solutions provides end-to-end advisory and execution support 
              to Alcoholic Beverages and FMCG organizations, led by one of the industry's 
              most experienced manufacturing professionals.
            </p>
          </div>
        </div>
      </section>

      {/* Founder Profile Section */}
      <section data-testid="founder-profile" className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-5 gap-12 lg:gap-20">
            {/* Image Column */}
            <div className="lg:col-span-2">
              <div className="sticky top-28">
                <div className="relative">
                  <div className="absolute -top-4 -left-4 w-full h-full bg-blue-600/10"></div>
                  <img 
                    src={FOUNDER_IMAGE}
                    alt="V Raghunath - Founder & Principal Consultant"
                    className="relative w-full aspect-[4/5] object-cover object-top"
                  />
                </div>
                <div className="mt-8 p-6 bg-slate-50 border border-slate-100">
                  <div className="flex items-center gap-4 mb-4">
                    <Award className="w-8 h-8 text-blue-600" />
                    <div>
                      <p className="font-semibold text-slate-900">37+ Years</p>
                      <p className="text-sm text-slate-500">Industry Experience</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <GraduationCap className="w-8 h-8 text-blue-600" />
                    <div>
                      <p className="font-semibold text-slate-900">McKinsey</p>
                      <p className="text-sm text-slate-500">Consulting Background</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Content Column */}
            <div className="lg:col-span-3 space-y-12">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold mb-4">Founder & Principal Consultant</p>
                <h2 className="font-serif text-4xl lg:text-5xl font-semibold text-slate-900 tracking-tight mb-6">
                  V Raghunath
                </h2>
                <div className="prose prose-lg max-w-none">
                  <p className="text-slate-600 leading-relaxed">
                    V Raghunath is a seasoned manufacturing and operations professional with over 
                    37 years of experience in the Alcoholic Beverages industry. His career 
                    spans leadership roles at India's most prestigious AlcoBev companies, including 
                    a 2-year business transformation project undertaken in collaboration with 
                    McKinsey & Company as a company representative.
                  </p>
                  <p className="text-slate-600 leading-relaxed mt-4">
                    With hands-on experience across the entire value chain—from distillation and 
                    blending to bottling, packaging, and supply chain—V Raghunath brings unparalleled 
                    expertise in transforming manufacturing operations and driving sustainable growth.
                  </p>
                </div>
              </div>

              {/* Career Timeline */}
              <div>
                <h3 className="font-serif text-2xl font-semibold text-slate-900 mb-8">Career Journey</h3>
                <div className="space-y-6">
                  {experience.map((item, index) => (
                    <div 
                      key={index}
                      className="flex gap-6 p-6 bg-slate-50 border border-slate-100 hover:border-blue-100 transition-colors"
                    >
                      <div className="flex-shrink-0 w-12 h-12 bg-slate-900 flex items-center justify-center">
                        <Briefcase className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-grow">
                        <div className="flex flex-wrap items-baseline gap-2 mb-2">
                          <h4 className="font-semibold text-slate-900 text-lg">{item.company}</h4>
                          <span className="text-sm text-blue-600">{item.period}</span>
                        </div>
                        <p className="text-slate-600 font-medium mb-1">{item.role}</p>
                        <p className="text-slate-500 text-sm">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission Section */}
      <section data-testid="vision-mission" className="py-20 lg:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Vision */}
            <div className="bg-slate-900 p-10 lg:p-14">
              <div className="w-16 h-16 bg-blue-600/20 flex items-center justify-center mb-8">
                <Eye className="w-8 h-8 text-blue-400" />
              </div>
              <h3 className="font-serif text-3xl font-semibold text-white mb-6">Our Vision</h3>
              <p className="text-slate-300 leading-relaxed text-lg">
                To be a trusted transformation partner for manufacturing-led organizations 
                by enabling safe, compliant, cost-competitive, and future-ready operations, 
                while creating long-term value through operational excellence, people 
                capability, and sustainability.
              </p>
            </div>

            {/* Mission */}
            <div className="bg-white p-10 lg:p-14 border border-slate-100">
              <div className="w-16 h-16 bg-blue-600/10 flex items-center justify-center mb-8">
                <Target className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="font-serif text-3xl font-semibold text-slate-900 mb-6">Our Mission</h3>
              <p className="text-slate-600 leading-relaxed text-lg">
                To deliver comprehensive manufacturing and supply chain solutions that 
                drive operational excellence, ensure regulatory compliance, and build 
                sustainable competitive advantage for AlcoBev and FMCG organizations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Objectives Section */}
      <section data-testid="objectives" className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold mb-4">What We Aim To Achieve</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-slate-900 tracking-tight">
              Our Strategic Objectives
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {objectives.map((objective, index) => (
              <div 
                key={index}
                className="flex gap-4 p-6 bg-slate-50 border-l-4 border-blue-600"
              >
                <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white flex items-center justify-center font-bold text-sm">
                  {index + 1}
                </div>
                <p className="text-slate-700 leading-relaxed">{objective}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="about-cta" className="py-20 lg:py-32 bg-slate-900">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-white tracking-tight mb-6">
            Partner with Industry Expertise
          </h2>
          <p className="text-lg text-slate-300 mb-10 max-w-2xl mx-auto">
            Leverage 35+ years of manufacturing excellence and strategic consulting 
            experience to transform your operations.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/services">
              <Button 
                data-testid="about-cta-services"
                className="bg-blue-600 text-white hover:bg-blue-700 rounded-none px-8 py-4 text-sm font-bold uppercase tracking-wider btn-shadow w-full sm:w-auto"
              >
                Explore Our Services
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button 
                data-testid="about-cta-contact"
                variant="outline"
                className="border-slate-600 text-white hover:bg-slate-800 rounded-none px-8 py-4 text-sm font-bold uppercase tracking-wider w-full sm:w-auto"
              >
                Get in Touch
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
